import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  FileDownload as DownloadIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const ManageClients = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success', 'error', 'info', 'warning'
  });

  const navigate = useNavigate();
  const token = localStorage.getItem('accessToken');

  const fetchClients = async () => {
    if (!token) {
      setError('No access token found. Please login.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/core/GetClients/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setClients(data);
      setFilteredClients(data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [token]);

  // Search / filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredClients(clients);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = clients.filter(client =>
      client.client_name?.toLowerCase().includes(term) ||
      client.client_code?.toLowerCase().includes(term) ||
      client.email?.toLowerCase().includes(term) ||
      client.mobile_no?.includes(term) ||
      client.client_id?.toString().includes(term) ||
      client.address?.toLowerCase().includes(term)
    );

    setFilteredClients(filtered);
  }, [searchTerm, clients]);

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/core/DeleteClient/${clientToDelete.client_id}/`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          setSnackbar({
            open: true,
            message: 'Permission denied or session expired. Please login again.',
            severity: 'error',
          });
        } else {
          setSnackbar({
            open: true,
            message: `Delete failed (HTTP ${response.status})`,
            severity: 'error',
          });
        }
        return;
      }

      // Success → remove from local state & close dialog
      setClients(prev => prev.filter(c => c.client_id !== clientToDelete.client_id));
      setFilteredClients(prev => prev.filter(c => c.client_id !== clientToDelete.client_id));
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      setSnackbar({
        open: true,
        message: 'Client deleted successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Delete error:', err);
      setSnackbar({
        open: true,
        message: 'Error while deleting client',
        severity: 'error',
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
  };

  const handleExport = async () => {
    if (!token) {
      setSnackbar({
        open: true,
        message: 'No access token found',
        severity: 'error',
      });
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/core/GetClients/export/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }

      // Most export endpoints return file directly (csv/xlsx)
      const blob = await response.blob();
      const contentType = response.headers.get('content-type');
      const extension = contentType?.includes('excel') || contentType?.includes('xlsx')
        ? 'xlsx'
        : 'csv';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_export_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Export completed successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Export error:', err);
      setSnackbar({
        open: true,
        message: 'Failed to export clients: ' + err.message,
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columns = [
    { id: 'sno',        label: 'S.No',         minWidth: 70,  align: 'center' },
    { id: 'client_name', label: 'Client Name',  minWidth: 180, align: 'left'   },
    { id: 'client_code', label: 'Code',         minWidth: 100, align: 'left'   },
    { id: 'email',       label: 'Email',        minWidth: 220, align: 'left'   },
    { id: 'mobile_no',   label: 'Mobile',       minWidth: 130, align: 'left'   },
    { id: 'address',     label: 'Address',      minWidth: 220, align: 'left'   },
    { id: 'website_link',label: 'Website',      minWidth: 160, align: 'left'   },
    { id: 'expiry_date', label: 'Expiry Date',  minWidth: 120, align: 'left'   },
    { id: 'is_active',   label: 'Active',       minWidth: 90,  align: 'center' },
    { id: 'actions',     label: 'Actions',      minWidth: 100, align: 'center' },
  ];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="error" variant="h6">{error}</Typography></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
        Manage Clients
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search by name, code, email, mobile..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
          }}
          sx={{ minWidth: { xs: '100%', sm: 340 }, flexGrow: 1, maxWidth: 500 }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Tooltip title="Export filtered clients as CSV">
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!filteredClients.length}>
              Export
            </Button>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/warals/manage-client/add-new-client')}
          >
            Add Client
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              {columns.map(col => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{ fontWeight: 600, minWidth: col.minWidth, whiteSpace: 'nowrap' }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? 'No matching clients found' : 'No clients yet'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client, index) => (
                <TableRow key={client.client_id} hover>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell>{client.client_name || '-'}</TableCell>
                  <TableCell>{client.client_code || '-'}</TableCell>
                  <TableCell>{client.email || '-'}</TableCell>
                  <TableCell>{client.mobile_no || '-'}</TableCell>
                  <TableCell>{client.address || '-'}</TableCell>
                <TableCell>
  {client.website_link ? (
    <a
      href={
        client.website_link.startsWith("http")
          ? client.website_link
          : `https://${client.website_link}`
      }
      target="_blank"
      rel="noopener noreferrer"
      title={client.website_link}
      style={{
        color: "#1976d2",
        textDecoration: "none",
      }}
    >
      {client.website_link.replace(/^https?:\/\//, "").replace(/\/$/, "")}
    </a>
  ) : (
    "-"
  )}
</TableCell>
                  <TableCell>{client.expiry_date || '-'}</TableCell>
                  <TableCell align="center">
                    {client.is_active ? (
                      <Typography color="success.main" fontWeight={500}>Yes</Typography>
                    ) : (
                      <Typography color="error.main" fontWeight={500}>No</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit client">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/warals/manage-client/edit-client/${client.client_id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete client">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(client)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete client
            <strong> {clientToDelete?.client_name} </strong>
            (Code: {clientToDelete?.client_code || '—'}) ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} data-app-cancel="true">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageClients;